/**
 * Servicio para gestión de productos/packs
 *
 * Este servicio maneja todas las operaciones relacionadas con productos:
 * - Obtener listados de productos
 * - Crear, actualizar y eliminar productos (admin)
 * - Gestionar estado de productos (activar/desactivar)
 * - Obtener estadísticas de productos
 */

import api from './api';

const ENDPOINTS = {
  PRODUCTS: '/products',
  AVAILABLE: '/products/available',
  FEATURED: '/products/featured',
  STATS: '/products/stats',
  PRICE_RANGE: '/products/price-range',
  CATEGORY: '/products/category',
  ACTIVATE: (id) => `/products/${id}/activate`,
  DEACTIVATE: (id) => `/products/${id}/deactivate`,
  CAN_PURCHASE: (id) => `/products/${id}/can-purchase`,
  NUEVOS_COMPRADORES_PAQUETE_IDS: '/products/nuevos-compradores/paquete-ids',
};

// Métodos adaptados para el sistema unificado
const productService = {
  /**
   * Obtener todos los productos con filtros opcionales
   */
  getAll: async (filters = {}) => {
    try {
      const params = new URLSearchParams();

      // Añadir filtros como parámetros de consulta
      Object.keys(filters).forEach(key => {
        if (filters[key] !== undefined && filters[key] !== null && filters[key] !== '') {
          params.append(key, filters[key]);
        }
      });

      const queryString = params.toString();
      const url = queryString ? `${ENDPOINTS.PRODUCTS}?${queryString}` : ENDPOINTS.PRODUCTS;

      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('❌ Error al obtener productos:', error);
      throw error;
    }
  },

  /**
   * Obtener producto por ID
   */
  getById: async (id) => {
    try {
      const response = await api.get(`${ENDPOINTS.PRODUCTS}/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener producto:', error);
      throw error;
    }
  },

  /**
   * Crear nuevo producto
   */
  create: async (productData) => {
    try {
      const response = await api.post(ENDPOINTS.PRODUCTS, productData);
      return response.data;
    } catch (error) {
      console.error('Error al crear producto:', error);
      throw error;
    }
  },

  /**
   * Actualizar producto
   */
  update: async (id, productData) => {
    try {
      const response = await api.put(`${ENDPOINTS.PRODUCTS}/${id}`, productData);
      return response.data;
    } catch (error) {
      console.error('Error al actualizar producto:', error);
      throw error;
    }
  },

  /**
   * Eliminar producto
   */
  delete: async (id) => {
    try {
      const response = await api.delete(`${ENDPOINTS.PRODUCTS}/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error al eliminar producto:', error);
      throw error;
    }
  },

  /**
   * Activar producto
   */
  activate: async (id) => {
    try {
      const response = await api.put(ENDPOINTS.ACTIVATE(id));
      return response.data;
    } catch (error) {
      console.error('Error al activar producto:', error);
      throw error;
    }
  },

  /**
   * Desactivar producto
   */
  deactivate: async (id) => {
    try {
      const response = await api.put(ENDPOINTS.DEACTIVATE(id));
      return response.data;
    } catch (error) {
      console.error('Error al desactivar producto:', error);
      throw error;
    }
  },

  /**
   * Toggle estado activo/inactivo
   */
  toggleActive: (id, currentStatus) => {
    return currentStatus ? productService.deactivate(id) : productService.activate(id);
  },

  /**
   * Obtener productos disponibles (público)
   */
  getAvailableProducts: async () => {
    try {
      const response = await api.get(ENDPOINTS.AVAILABLE);
      return response.data;
    } catch (error) {
      console.error('Error al obtener productos disponibles:', error);
      throw error;
    }
  },

  /**
   * Obtener estadísticas reales de un producto desde PostgreSQL
   */
  getRealStats: async (id) => {
    try {
      const response = await api.get(`${ENDPOINTS.PRODUCTS}/${id}/real-stats`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener estadísticas reales:', error);
      throw error;
    }
  },

  /**
   * Formatear precio
   */
  formatPrice: (price, currency = 'EUR') => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: currency,
    }).format(price);
  },

  /**
   * Formatear código de producto
   */
  formatProductCode: (code) => {
    return code.toUpperCase();
  },

  /**
   * Validar datos de producto
   */
  validateProductData: (productData) => {
    const errors = {};

    if (!productData.name || productData.name.trim().length === 0) {
      errors.name = 'El nombre es obligatorio';
    }

    if (!productData.code || productData.code.trim().length === 0) {
      errors.code = 'El código es obligatorio';
    } else if (!/^P\d+$/.test(productData.code.toUpperCase())) {
      errors.code = 'El código debe seguir el formato P1, P2, P3, etc.';
    }

    if (!productData.description || productData.description.trim().length === 0) {
      errors.description = 'La descripción es obligatoria';
    }

    if (!productData.price || productData.price <= 0) {
      errors.price = 'El precio debe ser mayor a 0';
    }

    if (!productData.sessions || productData.sessions < 1) {
      errors.sessions = 'Debe incluir al menos 1 jornada';
    }

    if (productData.sessionWindow && productData.sessionWindow < productData.sessions) {
      errors.sessionWindow = 'La ventana de sesiones debe ser mayor o igual al número de sesiones';
    }

    if (productData.postponements && productData.postponements < 0) {
      errors.postponements = 'Las posposiciones no pueden ser negativas';
    }

    if (productData.maxAge && productData.minAge && productData.maxAge < productData.minAge) {
      errors.maxAge = 'La edad máxima debe ser mayor o igual a la edad mínima';
    }

    if (productData.availableUntil && productData.availableFrom &&
        new Date(productData.availableUntil) <= new Date(productData.availableFrom)) {
      errors.availableUntil = 'La fecha de fin debe ser posterior a la fecha de inicio';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  },

  /**
   * Verificar si un usuario puede comprar un producto
   */
  canUserPurchaseProduct: async (id) => {
    try {
      const response = await api.get(ENDPOINTS.CAN_PURCHASE(id));
      return response.data;
    } catch (error) {
      console.error('Error al verificar compra de producto:', error);
      throw error;
    }
  },
};

/**
 * Obtener productos disponibles (público)
 */
export const getAvailableProducts = async () => {
  try {
    const response = await api.get(ENDPOINTS.AVAILABLE);
    return response.data;
  } catch (error) {
    console.error('Error al obtener productos disponibles:', error);
    throw error;
  }
};

/**
 * Obtener productos destacados
 */
export const getFeaturedProducts = async () => {
  try {
    const response = await api.get(ENDPOINTS.FEATURED);
    return response.data;
  } catch (error) {
    console.error('Error al obtener productos destacados:', error);
    throw error;
  }
};

/**
 * Obtener un producto por ID
 */
export const getProductById = async (id) => {
  try {
    const response = await api.get(`${ENDPOINTS.PRODUCTS}/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener producto:', error);
    throw error;
  }
};

/**
 * Crear un nuevo producto (admin)
 */
export const createProduct = async (productData) => {
  try {
    const response = await api.post(ENDPOINTS.PRODUCTS, productData);
    return response.data;
  } catch (error) {
    console.error('Error al crear producto:', error);
    throw error;
  }
};

/**
 * Actualizar un producto (admin)
 */
export const updateProduct = async (id, productData) => {
  try {
    const response = await api.put(`${ENDPOINTS.PRODUCTS}/${id}`, productData);
    return response.data;
  } catch (error) {
    console.error('Error al actualizar producto:', error);
    throw error;
  }
};

/**
 * Eliminar un producto (admin)
 */
export const deleteProduct = async (id) => {
  try {
    const response = await api.delete(`${ENDPOINTS.PRODUCTS}/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error al eliminar producto:', error);
    throw error;
  }
};

/**
 * Activar un producto (admin)
 */
export const activateProduct = async (id) => {
  try {
    const response = await api.put(ENDPOINTS.ACTIVATE(id));
    return response.data;
  } catch (error) {
    console.error('Error al activar producto:', error);
    throw error;
  }
};

/**
 * Desactivar un producto (admin)
 */
export const deactivateProduct = async (id) => {
  try {
    const response = await api.put(ENDPOINTS.DEACTIVATE(id));
    return response.data;
  } catch (error) {
    console.error('Error al desactivar producto:', error);
    throw error;
  }
};

/**
 * Obtener productos por categoría
 */
export const getProductsByCategory = async (category) => {
  try {
    const response = await api.get(`${ENDPOINTS.CATEGORY}/${category}`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener productos por categoría:', error);
    throw error;
  }
};

/**
 * Buscar productos por rango de precio
 */
export const getProductsByPriceRange = async (minPrice, maxPrice) => {
  try {
    const response = await api.get(`${ENDPOINTS.PRICE_RANGE}?minPrice=${minPrice}&maxPrice=${maxPrice}`);
    return response.data;
  } catch (error) {
    console.error('Error al buscar productos por precio:', error);
    throw error;
  }
};

/**
 * Obtener estadísticas de productos (admin)
 */
export const getProductStats = async () => {
  try {
    const response = await api.get(ENDPOINTS.STATS);
    return response.data;
  } catch (error) {
    console.error('Error al obtener estadísticas de productos:', error);
    throw error;
  }
};

/**
 * Verificar si un usuario puede comprar un producto
 */
export const canUserPurchaseProduct = async (id) => {
  try {
    const response = await api.get(ENDPOINTS.CAN_PURCHASE(id));
    return response.data;
  } catch (error) {
    console.error('Error al verificar compra de producto:', error);
    throw error;
  }
};

/**
 * Obtener IDs de paquetes solo para nuevos compradores
 */
productService.getPaqueteIdsNuevosCompradores = async () => {
  try {
    const response = await api.get(ENDPOINTS.NUEVOS_COMPRADORES_PAQUETE_IDS);
    return response.data;
  } catch (error) {
    console.error('❌ Error al obtener IDs de paquetes para nuevos compradores:', error);
    throw error;
  }
};

// Las funciones de utilidad ahora están incluidas en el objeto productService principal

// Exportar también las funciones individuales para compatibilidad
export const getProducts = productService.getAll;

// Exportar el servicio unificado como default
export default productService;
